import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import HorizontalLightLogo from "../../logos/logo-v2-light-horizontal.png";
import HorizontalDarkLogo from "../../logos/logo-v2-dark-horizontal.png";
import { useACTprepTheme } from "../../context/ACTprepContextPackage";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import { urls } from "../../utils/urls";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/auth/useAuth";

export default function AuthenticatingPage({ authenticate = false }) {
  const { theme } = useACTprepTheme();
  const { setToken } = useAuth();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const [showStuckMessage, setShowStuckMessage] = useState(false);

  const exchangeCodeForToken = useCallback(
    async (code) => {
      const tokenUrl = urls.sso.token();
      const clientId = process.env.REACT_APP_CLIENT_ID;
      const clientSecret = process.env.REACT_APP_CLIENT_SECRET; // if required

      // Retrieve the stored PKCE code verifier
      const codeVerifier = localStorage.getItem("pkce_code_verifier");
      if (!codeVerifier) {
        console.error(
          "No PKCE code verifier found in localStorage. Cannot complete token exchange."
        );
        return;
      }

      const data = new URLSearchParams();
      data.append("grant_type", "authorization_code");
      data.append("code", code);
      data.append("redirect_uri", urls.sso.redirect());
      data.append("client_id", clientId);
      if (clientSecret) {
        data.append("client_secret", clientSecret);
      }
      // Include the code verifier as required by PKCE.
      data.append("code_verifier", codeVerifier);

      try {
        const response = await fetch(tokenUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: data.toString(),
        });
        const tokenData = await response.json();
        if (tokenData.access_token) {
          // Store the tokens in cookies
          Cookies.set("access_token", tokenData.access_token, { expires: 1 });
          if (tokenData.refresh_token) {
            Cookies.set("refresh_token", tokenData.refresh_token, {
              expires: 7,
            });
          }
          setToken(tokenData.access_token);

          // Clean up the stored code verifier
          localStorage.removeItem("pkce_code_verifier");
          const redirect = localStorage.getItem("redirectAfterLogin");
          if (redirect) {
            navigate(redirect);
            return;
          }

          // Redirect the user to your dashboard or intended page.
          navigate("/");
        } else {
          console.error("Error during token exchange:", tokenData);
        }
      } catch (error) {
        console.error("Token exchange failed:", error);
      }
    },
    [navigate, setToken]
  );

  useEffect(() => {
    if (!authenticate) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    // Optionally, validate the state parameter here
    if (code) {
      exchangeCodeForToken(code);
    } else {
      console.error("No authorization code found in the URL.");
    }
  }, [authenticate, exchangeCodeForToken]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setShowStuckMessage(true);
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        backgroundColor: muiTheme.palette.background.paper,
      }}
    >
      <Box
        component="img"
        sx={{
          height: 100,
          display: {
            xs: "none",
            sm: "block",
          },
          margin: "2rem",
        }}
        alt="Your logo."
        src={theme === "dark" ? HorizontalLightLogo : HorizontalDarkLogo}
      />
      <Box
        sx={{
          marginTop: "30vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={showStuckMessage}
        message="Stuck here? Click this to reload."
        action={
          <Button
            size="small"
            onClick={() => {
              window.location.assign(window.location.origin);
            }}
          >
            Reload
          </Button>
        }
      />
    </Box>
  );
}
